<template>
  <div>
    <div
      class="relative bg-gradient-to-r from-theme-5 to-theme-4 py-10 px-6 md:px-14 rounded-t-2xl md:rounded-tr-none"
    >
      <SvgIconEdit
        class="w-16 h-16 absolute top-0 transform -translate-y-8 text-white"
      />
      <div class="text-white text-xl">{{ headerTitle }}</div>
      <div class="text-white text-sm font-light">
        All mandatory fields are indicated with red asterisks.
      </div>
    </div>
    <div class="p-3 sm:px-5 sm:py-5">
      <!-- BEGIN: Top Buttons -->
      <div class="flex justify-end transform -translate-y-9 sm:-translate-y-11">
        <VueButton
          class="button bg-gradient-to-b from-theme-11 to-theme-13 text-white relative pr-16 whitespace-nowrap"
          v-if="hasValidCertificates"
          @click="viewAllCertificates"
          :loading="processing"
        >
          View All Certificates
          <div
            class="h-full absolute right-0 top-0 pointer-events-none bg-gradient-to-b from-theme-14 to-theme-15 text-white rounded-md py-2.5 px-3"
          >
            <EyeIcon class="inline w-6 h-6" />
          </div>
        </VueButton>
      </div>
      <!-- END: Top Buttons -->
      <div class="section-divider mb-8">Training Details</div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-full sm:col-span-6">
          <VueInputGroup :validation-errors="validationErrors.get('title')">
            <template v-slot:label>
              <label class="block mb-1">Title</label>
            </template>
            <VueInput
              capitalize
              placeholder="Enter training title"
              v-model="formData.title"
              :validation-errors="validationErrors.get('title')"
            />
          </VueInputGroup>
        </div>
        <div class="col-span-full sm:col-span-6">
          <VueInputGroup :validation-errors="validationErrors.get('number')">
            <template v-slot:label>
              <label class="block mb-1">Number</label>
            </template>
            <VueInput
              uppercase
              placeholder="Enter training number"
              v-model="formData.number"
              :validation-errors="validationErrors.get('number')"
            />
          </VueInputGroup>
        </div>
        <div class="col-span-full sm:col-span-6">
          <VueInputGroup :validation-errors="validationErrors.get('date')">
            <template v-slot:label>
              <label class="block mb-1">Date</label>
            </template>
            <Litepicker
              v-model="formData.date"
              :validation-errors="validationErrors.get('date')"
            />
          </VueInputGroup>
        </div>
        <div class="col-span-full sm:col-span-6">
          <VueInputGroup :validation-errors="validationErrors.get('template')">
            <template v-slot:label>
              <label class="block mb-1">Template</label>
            </template>
            <VueSelect
              v-model="formData.template"
              placeholder="Select template"
              :validation-errors="validationErrors.get('template')"
            >
              <option
                v-for="templateOption in templateOptions"
                :key="`stage-${templateOption.value}`"
                :value="templateOption.value"
              >
                {{ templateOption.label }}
              </option>
            </VueSelect>
          </VueInputGroup>
        </div>
      </div>
      <div class="section-divider my-8">Description</div>
      <div class="grid grid-cols-12 gap-4 items-end">
        <div class="col-span-full">
          <VueInputGroup
            class="col-span-full"
            :validation-errors="validationErrors.get('description')"
          >
            <VueTextarea
              v-model="formData.description"
              :validation-errors="validationErrors.get('description')"
            />
          </VueInputGroup>
        </div>
      </div>

      <div class="section-divider my-8">Certificates</div>
      <div
        v-for="(certificate, index) in formData.certificates"
        :key="index"
        class="mb-6 p-4 border rounded-lg"
      >
        <div class="flex justify-between mb-3">
          <h3 class="font-medium">Certificate #{{ index + 1 }}</h3>
          <div class="flex gap-2">
            <Tippy
              tag="button"
              v-if="certificate.uuid"
              content="View Certificate"
              :options="{ placement: 'bottom' }"
              class="button button--sm rounded-none text-blue-500 text-center"
              @click="() => viewCertificate(certificate.uuid)"
            >
              <EyeIcon class="inline" />
            </Tippy>
            <Tippy
              v-if="formData.abilities?.can_update"
              tag="button"
              content="Delete"
              :options="{ placement: 'bottom' }"
              class="button button--sm rounded-none text-red-400 text-center"
              @click="() => removeCertificate(index)"
            >
              <Trash2Icon class="inline" />
            </Tippy>
          </div>
        </div>
        <div class="grid grid-cols-12 gap-4">
          <div class="col-span-full sm:col-span-6">
            <VueInputGroup
              :validation-errors="
                validationErrors.get(`certificates.${index}.number`)
              "
            >
              <template v-slot:label>
                <label class="block mb-1">Certificate Number</label>
              </template>
              <VueInput
                placeholder="Enter certificate number"
                v-model="certificate.number"
                :validation-errors="
                  validationErrors.get(`certificates.${index}.number`)
                "
              />
            </VueInputGroup>
          </div>
          <div class="col-span-full sm:col-span-6">
            <VueInputGroup
              :validation-errors="
                validationErrors.get(`certificates.${index}.name_surname`)
              "
            >
              <template v-slot:label>
                <label class="block mb-1">Name and Surname</label>
              </template>
              <VueInput
                placeholder="Enter name and surname"
                v-model="certificate.name_surname"
                :validation-errors="
                  validationErrors.get(`certificates.${index}.name_surname`)
                "
              />
            </VueInputGroup>
          </div>
          <div class="col-span-full sm:col-span-4">
            <VueInputGroup
              :validation-errors="
                validationErrors.get(`certificates.${index}.type`)
              "
            >
              <template v-slot:label>
                <label class="block mb-1">Certificate Type</label>
              </template>
              <VueSelect
                v-model="certificate.type"
                placeholder="Select certificate type"
                :options="certificateTypeOptions"
                :validation-errors="
                  validationErrors.get(`certificates.${index}.type`)
                "
              >
                <option
                  v-for="certificateTypeOption in certificateTypeOptions"
                  :key="`stage-${certificateTypeOption.value}`"
                  :value="certificateTypeOption.value"
                >
                  {{ certificateTypeOption.label }}
                </option>
              </VueSelect>
            </VueInputGroup>
          </div>
          <div class="col-span-full sm:col-span-4">
            <VueInputGroup
              :validation-errors="
                validationErrors.get(`certificates.${index}.email`)
              "
            >
              <template v-slot:label>
                <label class="block mb-1">Email</label>
              </template>
              <VueInput
                placeholder="Enter email address"
                v-model="certificate.email"
                :validation-errors="
                  validationErrors.get(`certificates.${index}.email`)
                "
              />
            </VueInputGroup>
          </div>
          <div class="col-span-full sm:col-span-4">
            <VueInputGroup
              :validation-errors="
                validationErrors.get(`certificates.${index}.phone`)
              "
            >
              <template v-slot:label>
                <label class="block mb-1">Phone</label>
              </template>
              <VueInput
                placeholder="Enter phone number"
                v-model="certificate.phone"
                :validation-errors="
                  validationErrors.get(`certificates.${index}.phone`)
                "
              />
            </VueInputGroup>
          </div>
        </div>
      </div>

      <div class="mb-6" v-if="formData.abilities?.can_update">
        <VueButton
          @click="addCertificate"
          class="button bg-gray-200 text-gray-700 px-4"
        >
          Add Certificate
        </VueButton>
      </div>

      <div class="flex justify-end mt-5">
        <VueButton
          v-if="formData.abilities?.can_update"
          :loading="processing"
          @click="onSubmit"
          class="button bg-gradient-to-b from-theme-14 to-theme-15 text-white shadow-lg px-10"
          >Save Training
        </VueButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useGeneralActions } from "@/utils/actions";
import { trainingService } from "@/services";
import VueTextarea from "@/global-components/form-elements/textarea/Main.vue";

interface Certificate {
  number: string;
  name_surname: string;
  type: string;
  uuid?: string;
  phone?: string;
  email?: string;
  emailed_at?: string;
}

interface Ability {
  can_update: boolean;
  can_delete: boolean;
}

interface PostTraining {
  number: string;
  title: string;
  description: string;
  date: string;
  template: string;
  certificates: Certificate[];
  abilities?: Ability;
}

export default defineComponent({
  name: "CreateTrainings",
  components: { VueTextarea },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const id = computed(() => route.params.id);
    const headerTitle = computed<string>(() => {
      if (id.value) return `EDIT TRAINING (#${id.value})`;
      return "CREATE TRAINING";
    });

    const templateOptions = ref([
      { label: "Basic Template", value: "certificate-template-3.pdf" },
    ]);

    const certificateTypeOptions = ref([
      { label: "Participation", value: "participation" },
      { label: "Successful Completion", value: "Successful Completion" },
      { label: "Achievement", value: "Achievement" },
      { label: "Appreciation", value: "Appreciation" },
    ]);

    const { fetchRecord, createOrUpdate } = useGeneralActions();
    const { loading, data, fetch } = fetchRecord({
      fetchApiMethod: trainingService.show,
      errorCallback() {
        router.go(-1);
      },
      successCallback() {
        setFormData(data.value);
      },
    });

    const {
      processing,
      formData,
      setFormData,
      validationErrors,
      submit,
    } = createOrUpdate({
      createApiMethod: trainingService.store,
      updateApiMethod: trainingService.update,
      successCallback() {
        router.go(-1);
      },
      params: {
        number: "",
        title: "",
        description: "",
        date: "",
        template: "",
        certificates: [], // Initialize with empty array here
        abilities: {
          can_update: true,
          can_delete: true,
        },
      } as PostTraining,
    });

    const addCertificate = () => {
      // Ensure certificates array exists before pushing
      if (!formData.certificates) {
        formData.certificates = [];
      }
      formData.certificates.push({
        number: "",
        name_surname: "",
        type: "",
        email: "",
        phone: "",
      });
    };

    const removeCertificate = (index: number) => {
      formData.certificates.splice(index, 1);
    };

    watch(id, (val) => fetch(val).finally(), { flush: "post" });
    onMounted(() => {
      if (id.value) {
        fetch(id.value).finally();
      } else {
        // Make sure to initialize certificates as an array if it doesn't exist
        console.log(formData);
        if (!formData.certificates) {
          formData.certificates = [];
        }

        // Add the first certificate if there are none
        if (formData.certificates.length === 0) {
          addCertificate();
        }
      }
    });

    const onSubmit = () => submit(id.value);

    const viewCertificate = (uuid: string) => {
      if (uuid) {
        window.open(`https://certificates.smiic.org/?uuid[]=${uuid}`, "_blank");
      }
    };

    const hasValidCertificates = computed(() => {
      return (
        formData.certificates &&
        formData.certificates.some((cert: Certificate) => cert.uuid)
      );
    });

    const viewAllCertificates = () => {
      const uuids = formData.certificates
        .filter((cert: Certificate) => cert.uuid)
        .map((cert: Certificate) => `uuid[]=${cert.uuid}`)
        .join("&");

      if (uuids) {
        window.open(`https://certificates.smiic.org/?${uuids}`, "_blank");
      }
    };

    return {
      headerTitle,
      loading,
      processing,
      formData,
      validationErrors,
      onSubmit,
      templateOptions,
      certificateTypeOptions,
      addCertificate,
      removeCertificate,
      viewCertificate,
      viewAllCertificates,
      hasValidCertificates,
    };
  },
});
</script>
